<script setup lang="ts">
import { dir } from '@@/bits/current_dir'
import { getHyphenatedCurrentLocale } from '@@/bits/current_locale'
import device from '@@/bits/device'
import { isAppUsing } from '@@/bits/flip'
import { defineAsyncComponent } from '@@/bits/vue'
import { useAccessibilitySettingsStore } from '@@/pinia/accessibility_settings_store'
import { useAppBarAccountStore } from '@@/pinia/app_bar_account'
import { useExpandedPostStore } from '@@/pinia/expanded_post'
import { useLibraryPlansStore } from '@@/pinia/library_plans'
import { PadletSelectionIntent } from '@@/pinia/padlet_picker'
import { usePersonalPlansStore } from '@@/pinia/personal_plans_store'
import { useSurfaceStore } from '@@/pinia/surface'
import { useCommentEditMode, useCommentsStore } from '@@/pinia/surface_comments'
import { useSurfaceContainerSizeStore } from '@@/pinia/surface_container_size'
import { useSurfaceExpandedCommentStore } from '@@/pinia/surface_expanded_comment'
import { useSurfaceFreezePadletPanel } from '@@/pinia/surface_freeze_padlet_panel'
import { useSurfaceGuestStore } from '@@/pinia/surface_guest_store'
import { useSurfaceOnboardingDemoPadletPanelStore } from '@@/pinia/surface_onboarding_demo_padlet_panel_store'
import { useSurfacePanelsStore } from '@@/pinia/surface_panels'
import { useSurfacePermissionsStore } from '@@/pinia/surface_permissions'
import { useSurfacePostActionStore } from '@@/pinia/surface_post_action'
import { useSurfacePostPropertiesStore } from '@@/pinia/surface_post_properties'
import { useReactionsStore } from '@@/pinia/surface_reactions'
import { useSurfaceRequireNoticeStore } from '@@/pinia/surface_require_notice_store'
import { useSurfaceScreenRecorderStore } from '@@/pinia/surface_screen_recorder_store'
import { useSurfaceSectionsStore } from '@@/pinia/surface_sections'
import { useSurfaceSettingsStore } from '@@/pinia/surface_settings'
import { useSurfaceTranslationsStore } from '@@/pinia/surface_translations_store'
import { useSurfaceUserContributorsStore } from '@@/pinia/surface_user_contributors'
import { useWindowSizeStore } from '@@/pinia/window_size'
import type { Id } from '@@/types'
import usePostDuplicateAndTransfer from '@@/vuecomposables/post_duplicate_and_transfer'
import { installTranslations } from '@padlet/universal-post-editor'
import { storeToRefs } from 'pinia'
import { computed, onMounted, ref } from 'vue'

// IMPORTANT: If you're adding a new panel here, please also add it to the
// `isOverlayVisible` getter in `app/javascript/vuexstore/surface/index.ts`
const SurfaceContributors = defineAsyncComponent(() => import('@@/vuecomponents/SurfaceContributors.vue'))
const SurfaceSearchBar = defineAsyncComponent(() => import('@@/vuecomponents/SurfaceSearchBar.vue'))
const SurfaceDetailsPanel = defineAsyncComponent(() => import('@@/vuecomponents/SurfaceDetailsPanel.vue'))
const SurfaceSettingsPanel = defineAsyncComponent(() => import('@@/vuecomponents/SurfaceSettingsPanel.vue'))
const SurfaceSettingsVideoRecorder = defineAsyncComponent(
  () => import('@@/vuecomponents/SurfaceSettingsVideoRecorder.vue'),
)
const SurfaceSettingsScreenRecorder = defineAsyncComponent(
  () => import('@@/vuecomponents/SurfaceSettingsScreenRecorder.vue'),
)
const SurfaceSharePanel = defineAsyncComponent(() => import('@@/vuecomponents/SurfaceSharePanel.vue'))
const SurfaceRemakePanel = defineAsyncComponent(() => import('@@/vuecomponents/SurfaceRemakePanel.vue'))
const SurfacePanelPostExpanded = defineAsyncComponent(() => import('@@/vuecomponents/SurfacePanelPostExpanded.vue'))
const SurfacePanelPostActionMenuNew = defineAsyncComponent(
  () => import('@@/vuecomponents/SurfacePanelPostActionMenu.vue'),
)
const SurfacePhoneNumberFieldMethodMenu = defineAsyncComponent(
  () => import('@@/vuecomponents/SurfacePhoneNumberFieldMethodMenu.vue'),
)
const SurfaceSectionActionMenu = defineAsyncComponent(() => import('@@/vuecomponents/SurfaceSectionActionMenu.vue'))
const SurfaceCommentActionMenu = defineAsyncComponent(() => import('@@/vuecomponents/SurfaceCommentActionMenu.vue'))
const SurfaceComposerModalList = defineAsyncComponent(() => import('@@/vuecomponents/SurfaceComposerModalList.vue'))
const UpgradeRequired = defineAsyncComponent(() => import('@@/vuecomponents/upgrade_required.vue'))
const SurfaceDropdownReactionStar = defineAsyncComponent(
  () => import('@@/vuecomponents/SurfaceDropdownReactionStar.vue'),
)
const SurfaceDropdownReactionGrade = defineAsyncComponent(
  () => import('@@/vuecomponents/SurfaceDropdownReactionGrade.vue'),
)
const DialogPermitNotification = defineAsyncComponent(() => import('@@/vuecomponents/DialogPermitNotification.vue'))
const SurfaceDialogExtensionRequired = defineAsyncComponent(
  () => import('@@/vuecomponents/SurfaceDialogExtensionRequired.vue'),
)
const SurfaceSectionRenameModal = defineAsyncComponent(() => import('@@/vuecomponents/SurfaceSectionRenameModal.vue'))
const SurfaceCommentEditDrawer = defineAsyncComponent(() => import('@@/vuecomponents/SurfaceCommentEditDrawer.vue'))
const MembershipTierUpgradeModal = defineAsyncComponent(() => import('@@/vuecomponents/MembershipTierUpgradeModal.vue'))
const SurfaceEditBookmarkModal = defineAsyncComponent(() => import('@@/vuecomponents/SurfaceEditBookmarkModal.vue'))
const MembershipLibraryTierUpgradeModal = defineAsyncComponent(
  () => import('@@/vuecomponents/MembershipLibraryTierUpgradeModal.vue'),
)
const UserMenu = defineAsyncComponent(() => import('@@/vuecomponents/UserMenu.vue'))
const SurfaceGuestIdCard = defineAsyncComponent(() => import('@@/vuecomponents/SurfaceGuestIdCard.vue'))
const SurfaceActivityPanel = defineAsyncComponent(() => import('@@/vuecomponents/SurfaceActivityPanel.vue'))
const SurfaceReportPanel = defineAsyncComponent(() => import('@@/vuecomponents/SurfaceReportPanel.vue'))
const SurfaceDevelopersPanel = defineAsyncComponent(() => import('@@/vuecomponents/SurfaceDevelopersPanel.vue'))
const SurfaceFreezePadletPanel = defineAsyncComponent(() => import('@@/vuecomponents/SurfaceFreezePadletPanel.vue'))
const SurfaceQrCodeDialog = defineAsyncComponent(() => import('@@/vuecomponents/SurfaceQrCodeDialog.vue'))
const SurfaceAutomationsPanel = defineAsyncComponent(() => import('@@/vuecomponents/SurfaceAutomationsPanel.vue'))
const SurfacePadletPickerV2 = defineAsyncComponent(() => import('@@/vuecomponents/SurfacePadletPickerV2.vue'))
const SurfacePanelCommentImageExpanded = defineAsyncComponent(
  () => import('@@/vuecomponents/SurfacePanelCommentImageExpanded.vue'),
)
const SurfaceLanguagePanel = defineAsyncComponent(() => import('@@/vuecomponents/SurfaceLanguagePanel.vue'))
const AccessibilitySettingsModal = defineAsyncComponent(() => import('@@/vuecomponents/AccessibilitySettingsModal.vue'))
const SurfaceScreenRecorder = defineAsyncComponent(() => import('@@/vuecomponents/SurfaceScreenRecorder.vue'))
const SurfacePhotoAlbumContentPicker = defineAsyncComponent(
  () => import('@@/vuecomponents/SurfacePhotoAlbumContentPicker.vue'),
)
const SurfacePhotoAlbumEditorDialog = defineAsyncComponent(
  () => import('@@/vuecomponents/SurfacePhotoAlbumEditorDialog.vue'),
)

withDefaults(
  defineProps<{
    reactionIdsByPostId: Record<Id, Id[]>
    commentIdsByPostId: Record<Id, Id[]>
  }>(),
  {
    reactionIdsByPostId: () => ({}),
    commentIdsByPostId: () => ({}),
  },
)

const { shouldEditCommentsInModal } = useCommentEditMode()

const isApp = device.app

const surfaceStore = useSurfaceStore()
const {
  xWallQuotaExceeded,
  fileThatExceededSize,
  isNativeMenu,
  xPermitNotification,
  isFirstView,
  xSearchBar,
  user,
  isMobileLayout,
  xNewComposerModalPanel,
  canUseSections,
  xReportPanel,
  xDevelopersPanel,
  xSettingsPanel,
  hasSidePanelOutsideSurface,
  xEditBookmarksDialog,
} = storeToRefs(surfaceStore)
const { xContributorsList } = storeToRefs(useSurfaceUserContributorsStore())
const { postUnderActionCid, xTransferPostPanel, postBeingTransferredCid, xCopyPostPanel, postBeingCopiedCid } =
  storeToRefs(useSurfacePostActionStore())
const { xExpandedPostPanel } = storeToRefs(useExpandedPostStore())
const { xUserMenu } = storeToRefs(useAppBarAccountStore())
const { xGuestIdCard } = storeToRefs(useSurfaceGuestStore())
const { xUpgradeModal: xLibraryUpgradeModal } = storeToRefs(useLibraryPlansStore())
const { xUpgradeModal } = storeToRefs(usePersonalPlansStore())
const { commentUnderActionId } = storeToRefs(useCommentsStore())
const { sectionUnderActionId, xSectionRenameModal } = storeToRefs(useSurfaceSectionsStore())
const { isSmallerThanTabletPortrait } = storeToRefs(useWindowSizeStore())
const { isContainerSmallerThanTabletLandscape } = storeToRefs(useSurfaceContainerSizeStore())
const { xSurfaceFreezePanel } = storeToRefs(useSurfaceFreezePadletPanel())
const {
  xSurfaceDetailsPanel,
  xPhoneFieldMethodPopover,
  xQrModal,
  xSurfaceActivityPanel,
  xSurfaceSharePanel,
  xSurfaceRemakePanel,
  xSurfaceAutomationsPanel,
  xPhotoAlbumImagePicker,
  xPhotoAlbumEditorDialog,
} = storeToRefs(useSurfacePanelsStore())
const { originalReactionData, postIdBeingReactedTo } = storeToRefs(useReactionsStore())
const { amIRegistered } = storeToRefs(useSurfacePermissionsStore())
const { xExpandedCommentImagePanel } = storeToRefs(useSurfaceExpandedCommentStore())

const { demoPadletPanelVisibility, isDemoPadletPanelDesktop } = storeToRefs(useSurfaceOnboardingDemoPadletPanelStore())
const { xSurfaceLanguagePanel } = storeToRefs(useSurfaceTranslationsStore())

const surfaceGuestStore = useSurfaceGuestStore()
const accessibilitySettingsStore = useAccessibilitySettingsStore()
const surfaceScreenRecorderStore = useSurfaceScreenRecorderStore()

const xCurtain = computed(() => {
  if (hasSidePanelOutsideSurface.value && isSmallerThanTabletPortrait.value) return false
  return xSurfaceSharePanel.value || xCopyPostPanel.value || xTransferPostPanel.value
})

const xCommentMenu = computed(() => {
  return commentUnderActionId.value && !isNativeMenu.value
})

const hasInstalledTranslations = ref(false)

onMounted(() => {
  // Need to install translations for the post editor before it's rendered.
  // We do it here so it's installed the earliest possible.
  installTranslations(getHyphenatedCurrentLocale()).then(() => {
    hasInstalledTranslations.value = true
  })

  if (isAppUsing('onboardingPanel')) {
    return
  }
  if (isFirstView.value) {
    showSettingsPanel()
  }
})

const surfaceRequireNoticeStore = useSurfaceRequireNoticeStore()
const { xExtensionRequired } = storeToRefs(surfaceRequireNoticeStore)
const { hideExtensionRequired } = surfaceRequireNoticeStore
const { hideWallQuotaExceeded, hidePermitNotification } = surfaceStore
const { endPostTransfer, endPostCopy } = useSurfacePostActionStore()
const { closeUpgradeModal: closeLibraryUpgradeModal } = useLibraryPlansStore()
const { showSettingsPanel } = useSurfaceSettingsStore()
const { closeExpandedCommentImagePanel } = useSurfaceExpandedCommentStore()

const surfacePostPropertiesStore = useSurfacePostPropertiesStore()
const { xVideoRecorderSettingsPanel, xScreenRecorderSettingsPanel } = storeToRefs(surfacePostPropertiesStore)
const { hideVideoRecorderSettingsPanel, hideScreenRecorderSettingsPanel } = surfacePostPropertiesStore

function closePlanUpgradeModal() {
  usePersonalPlansStore().closePlanUpgradeModal()
}

const { duplicatePost, transferPost, isCopyingPost, isTransferringPost } = usePostDuplicateAndTransfer()
</script>

<template>
  <div id="surface-panels" class="surface-panels">
    <div v-if="xCurtain" class="curtain is-animated fade-in" />
    <UserMenu
      v-if="xUserMenu"
      :user="user"
      :anchor-left-offset="isMobileLayout ? 22 : 16"
      :anchor-top-offset="isMobileLayout ? 4 : 16"
      :is-used-on-surface="true"
    />
    <SurfaceGuestIdCard
      v-if="
        surfaceGuestStore.shouldEnableAnonymousAttribution &&
        !amIRegistered &&
        xGuestIdCard &&
        !isSmallerThanTabletPortrait
      "
    />
    <SurfaceContributors v-if="xContributorsList" />
    <SurfaceSearchBar
      v-if="
        !isApp && isContainerSmallerThanTabletLandscape && xSearchBar && demoPadletPanelVisibility.showSurfaceSearchBar
      "
      :class="[
        'fixed start-0 end-0',
        hasSidePanelOutsideSurface && isSmallerThanTabletPortrait ? '-bottom-1' : 'bottom-0',
      ]"
      :should-animate-on-appear="true"
    />
    <SurfaceSettingsPanel v-if="xSettingsPanel" />
    <SurfaceSharePanel v-if="xSurfaceSharePanel" />
    <SurfaceQrCodeDialog v-if="xQrModal" @close="xQrModal = false" />
    <SurfaceRemakePanel v-if="xSurfaceRemakePanel && isAppUsing('remakePanelV2')" />
    <SurfacePanelPostExpanded
      v-if="xExpandedPostPanel && !isDemoPadletPanelDesktop"
      :reaction-ids-by-post-id="reactionIdsByPostId"
      :comment-ids-by-post-id="commentIdsByPostId"
    />
    <SurfacePanelCommentImageExpanded v-if="xExpandedCommentImagePanel" @close="closeExpandedCommentImagePanel" />
    <SurfacePanelPostActionMenuNew
      v-if="postUnderActionCid && !isNativeMenu && !isDemoPadletPanelDesktop"
      :post-cid="postUnderActionCid"
    />
    <SurfaceCommentActionMenu
      v-if="xCommentMenu"
      :comment-id="commentUnderActionId"
      :usage-context="xExpandedPostPanel ? 'postExpandedView' : 'postView'"
    />
    <SurfacePadletPickerV2
      v-if="isAppUsing('padletPickerV2') && isTransferringPost"
      :padlet-selection-intent="PadletSelectionIntent.TransferPost"
      :post-cid="postBeingTransferredCid"
      @select="transferPost"
      @close="endPostTransfer"
    />
    <SurfacePadletPickerV2
      v-if="isAppUsing('padletPickerV2') && isCopyingPost"
      :padlet-selection-intent="PadletSelectionIntent.CopyPost"
      :post-cid="postBeingCopiedCid"
      @select="duplicatePost"
      @close="endPostCopy"
    />
    <SurfaceComposerModalList v-if="xNewComposerModalPanel && hasInstalledTranslations" />
    <!-- Aug 2021: replaced upgrade-required with `MembershipTierUpgradeModal` however not removed because potentially still used by very old app versions. -->
    <upgrade-required v-if="xWallQuotaExceeded" reason="walls" @close="hideWallQuotaExceeded" />

    <SurfaceCommentEditDrawer v-if="shouldEditCommentsInModal" />

    <!-- Any content pickers in surface should be rendered inside this div -->
    <!-- NOTE: Place modals that should appear beneath the content picker above this div. Place modals that should appear over the content picker below this div. -->
    <!-- Explanation: Since all modals have the same z-index, we control what gets rendered above the content picker and what gets rendered below the content picker by their relative order in the DOM -->
    <div id="content-picker-portal-target"></div>

    <SurfaceSettingsVideoRecorder
      v-if="xVideoRecorderSettingsPanel"
      :x-scrim="false"
      @close="hideVideoRecorderSettingsPanel"
    />

    <SurfaceSettingsScreenRecorder
      v-if="xScreenRecorderSettingsPanel"
      :x-scrim="false"
      @close="hideScreenRecorderSettingsPanel"
    />

    <MembershipTierUpgradeModal
      v-if="xUpgradeModal"
      :user="user"
      :file-that-exceeded-size="fileThatExceededSize"
      @cancel="closePlanUpgradeModal"
    />
    <MembershipLibraryTierUpgradeModal
      v-if="xLibraryUpgradeModal"
      :file-that-exceeded-size="fileThatExceededSize"
      @cancel="closeLibraryUpgradeModal"
    />
    <SurfaceDropdownReactionGrade
      v-if="postIdBeingReactedTo && originalReactionData.type == 'grade'"
      :dir="dir()"
      :usage-context="xExpandedPostPanel ? 'postExpandedView' : 'postView'"
    />
    <SurfaceDropdownReactionStar
      v-if="postIdBeingReactedTo && originalReactionData.type == 'star'"
      :dir="dir()"
      :usage-context="xExpandedPostPanel ? 'postExpandedView' : 'postView'"
    />

    <SurfaceDialogExtensionRequired v-if="xExtensionRequired" @close="hideExtensionRequired" />
    <DialogPermitNotification
      v-if="xPermitNotification && demoPadletPanelVisibility.showDialogPermitNotification"
      @close="hidePermitNotification"
    />
    <SurfaceEditBookmarkModal v-if="xEditBookmarksDialog" />
    <SurfaceSectionRenameModal v-if="xSectionRenameModal && canUseSections" />
    <SurfaceActivityPanel v-if="xSurfaceActivityPanel" />
    <SurfaceSectionActionMenu v-if="sectionUnderActionId" />
    <SurfaceReportPanel v-if="xReportPanel" />
    <SurfaceDevelopersPanel v-if="xDevelopersPanel" />
    <SurfaceFreezePadletPanel v-if="xSurfaceFreezePanel" />
    <SurfaceDetailsPanel v-if="xSurfaceDetailsPanel" />
    <SurfaceAutomationsPanel v-if="xSurfaceAutomationsPanel" />
    <SurfaceLanguagePanel v-if="xSurfaceLanguagePanel" />
    <SurfacePhoneNumberFieldMethodMenu v-if="xPhoneFieldMethodPopover" />
    <AccessibilitySettingsModal
      v-if="isAppUsing('pageSpecificA11ySettings')"
      :visible="accessibilitySettingsStore.xAccessibilitySettingsModal"
      :portal-selector="'#surface-container'"
      :user="user"
      @close="accessibilitySettingsStore.closeAccessibilitySettingsModal"
    />
    <SurfaceScreenRecorder v-if="isAppUsing('screenRecorderV2') && surfaceScreenRecorderStore.isVisible" />
    <SurfacePhotoAlbumEditorDialog v-if="isAppUsing('photoAlbum') && xPhotoAlbumEditorDialog" />
    <SurfacePhotoAlbumContentPicker v-if="isAppUsing('photoAlbum') && xPhotoAlbumImagePicker" />
  </div>
</template>

<style lang="scss" scoped>
@import '@@/styles/3/modules/all';
.surface-panels {
  .curtain {
    position: fixed;
    top: 0;
    left: 0;
    width: surfaceContainerWidth;
    @include vh100;
    z-index: $sidepanel-zindex - 1;
    background-color: $translucent-backdrop-color;
    -webkit-overflow-scrolling: touch;
    touch-action: manipulation;
    @include respondToTouch {
      cursor: pointer;
    }
  }
}
</style>
